import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CurriculmnSchoolAdmin } from "../../../services/operations/chatbotAPI";
import { useDispatch } from "react-redux";
import { fetchCurriculumByClassAdmin } from "../../../services/operations/curriculumAPI";
import toast from "react-hot-toast";
import { ObjectId } from "bson";
import ReactMarkdown from "react-markdown";
import "katex/dist/katex.min.css";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

const classes = ["6", "7", "8", "9", "10"];

const AICurriculumAssistant = ({ user }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // --- Dropdown selections ---
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedChapter, setSelectedChapter] = useState("");

  const [curriculumData, setCurriculumData] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);

  // --- Chat state ---
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const conversationId =  new ObjectId().toHexString()
  
  const [isLoading, setIsLoading] = useState(false);

  const chatEndRef = useRef(null);
  const textareaRef = useRef(null);

  // --- Fetch curriculum for selected class ---
  useEffect(() => {
    const loadCurriculum = async () => {
      if (!selectedClass) return;
      const data = await dispatch(fetchCurriculumByClassAdmin(selectedClass));
      if (data) {
        setCurriculumData(data);
        if (data.subjects) {
          const subjectList = Object.keys(data.subjects).map((key) => ({
            id: key,
            name: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " "),
          }));
          setSubjects(subjectList);
        }
      }
    };
    loadCurriculum();
  }, [selectedClass, dispatch]);

  // --- Update chapters when subject changes ---
  useEffect(() => {
    if (
      selectedSubject &&
      curriculumData?.subjects?.[selectedSubject]?.chapters
    ) {
      setChapters(curriculumData.subjects[selectedSubject].chapters);
    } else {
      setChapters([]);
    }
  }, [selectedSubject, curriculumData]);

  // --- Scroll chat to bottom ---
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

    // --- Auto-resize textarea ---
  useEffect(() => {
    if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [input]);

  // --- Send message ---
  const handleSend = async () => {
    if (!input.trim()) return;
    if (!selectedClass || !selectedSubject || !selectedChapter) {
      return toast.error("Please select class, subject, and chapter.");
    }

    const userMessage = { role: "user", text: input };
    setMessages((prev) => [...prev, userMessage]);
    const query = input;
    setInput("");
    setIsLoading(true);

    try {
      const formattedClass = `cbse-${selectedClass.padStart(2, "0")}`;

      const result = await CurriculmnSchoolAdmin({
        query,
        conversationId,
        className: formattedClass,
        subjectName: selectedSubject,
        chapterName: selectedChapter,
      });

      if (result) {
        const aiMessage = { role: "assistant", text: result.response };
        setMessages((prev) => [...prev, aiMessage]);
      }
    } catch (error) {
      toast.error("Failed to get AI response");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
    }
  };

  const preprocessContent = (content) => {
    if (!content) return "";
    let processed = content;

    processed = processed.replace(/^(#{1,6})([^\s#])/gm, "$1 $2");
    processed = processed.replace(/^(\s*[*+-])([^\s])/gm, "$1 $2");
    processed = processed.replace(/^(\s*\d+\.)([^\s])/gm, "$1 $2");
    processed = processed.replace(
      /([^\n])\n(\s*([*+-]|\d+\.)\s)/gm,
      "$1\n\n$2"
    );
    processed = processed.replace(/\n{3,}/g, "\n\n");
    return processed.trim();
  };

  const fixMathErrors = (content) => {
    if (!content) return "";
    let processed = content;

    processed = processed.replace(/\\\(([\s\S]*?)\\\)/g, "$$$1$");
    processed = processed.replace(/\\\[([\s\S]*?)\\\]/g, "$$$$$1$$$$");
    processed = processed.replace(
      /(?<!\\)(\$\$?)([\s\S]+?)\1/g,
      (match, delimiter, mathExpression) => {
        const singleLineExpression = mathExpression
          .replace(/\s*\n\s*/g, " ")
          .trim();
        return `${delimiter}${singleLineExpression}${delimiter}`;
      }
    );
    return processed;
  };

  const markdownComponents = useMemo(
    () => ({
      p: ({ children }) => <p className="mb-4 leading-relaxed text-base">{children}</p>,
      ul: ({ children }) => <ul className="mb-6 mt-2 ml-6 space-y-2 list-disc list-outside">{children}</ul>,
      ol: ({ children }) => <ol className="mb-6 mt-2 ml-6 space-y-2 list-decimal list-outside">{children}</ol>,
      li: ({ children }) => <li className="leading-relaxed pl-2">{children}</li>,
      h1: ({ children }) => <h1 className="text-xl md:text-2xl font-bold mt-8 mb-6 pb-3 border-b-2">{children}</h1>,
      h2: ({ children }) => <h2 className="text-lg md:text-xl font-semibold mt-7 mb-4">{children}</h2>,
      h3: ({ children }) => <h3 className="text-base md:text-lg font-semibold mt-6 mb-3">{children}</h3>,
      strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
      table: ({ children }) => (
        <div className="overflow-x-auto my-6 rounded-lg border border-gray-300">
          <table className="w-full text-sm border-collapse">{children}</table>
        </div>
      ),
      thead: ({ children }) => <thead className="bg-gray-100">{children}</thead>,
      tbody: ({ children }) => <tbody>{children}</tbody>,
      tr: ({ children }) => <tr className="border-b border-gray-200 last:border-b-0 hover:bg-gray-50">{children}</tr>,
      th: ({ children }) => <th className="text-left font-semibold p-3 border-r border-gray-200 last:border-r-0">{children}</th>,
      td: ({ children }) => <td className="p-3 border-r border-gray-200 last:border-r-0">{children}</td>,
    }),
    []
  );

  return (
    <div className="bg-white p-4 sm:p-6 md:p-8 rounded-2xl shadow-sm h-screen flex flex-col">
      {/* --- HEADER --- */}
      <div className="flex-shrink-0">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">
            {t("aiCurriculumAssistant.title")}
          </h2>

          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <select
              className="w-full sm:w-auto border p-2 rounded-lg text-sm"
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
            >
              <option value="">{t("aiCurriculumAssistant.selectClass")}</option>
              {classes.map((cls) => (
                          <option key={cls} value={cls}>
                {t("aiCurriculumAssistant.classLabel", { cls })}
              </option>
              ))}
            </select>

            <select
              className="w-full sm:w-auto border p-2 rounded-lg text-sm"
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              disabled={!subjects.length}
            >
              <option value="">{t("aiCurriculumAssistant.selectSubject")}</option>
              {subjects.map((sub) => (
                <option key={sub.id} value={sub.id}>{sub.name}</option>
              ))}
            </select>

            <select
              value={selectedChapter}
              onChange={(e) => setSelectedChapter(e.target.value)}
              disabled={!selectedSubject || chapters.length === 0}
              className="w-full sm:flex-1 p-2 rounded-md bg-white text-gray-800 border border-gray-300 text-sm focus:border-blue-500 outline-none disabled:opacity-50 truncate"
              title={selectedChapter}
            >
            <option value="">
              {!selectedSubject
                ? t("aiCurriculumAssistant.selectSubjectFirst")
                : chapters.length === 0
                ? t("aiCurriculumAssistant.noChapters")
                : t("aiCurriculumAssistant.selectChapter")}
            </option>
              {chapters.map((chapter, idx) => (
                <option key={idx} value={chapter}>
                  {chapter.length > 50 ? chapter.slice(0, 50) + "..." : chapter}
                </option>
              ))}
            </select>
          </div>
      </div>

      {/* --- CHAT HISTORY (SCROLLABLE AREA) --- */}
      {/* --- THIS IS THE FIX ---: Added min-h-0 to make flexbox scrolling work correctly */}
      <div className="flex-1 bg-gray-50 rounded-lg border border-gray-200 overflow-y-auto p-4 space-y-4 min-h-0">
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`px-3 py-2 sm:p-3 rounded-lg max-w-[85%] sm:max-w-2xl lg:max-w-4xl ${
                msg.role === "user"
                  ? "bg-indigo-500 text-white"
                  : "bg-white text-gray-800 border"
              }`}
            >
              {msg.text && msg.text.trim() && (
                <ReactMarkdown
                  remarkPlugins={[remarkMath, remarkGfm]}
                  rehypePlugins={[rehypeKatex, rehypeRaw]}
                  components={markdownComponents}
                >
                  {fixMathErrors(preprocessContent(msg.text))}
                </ReactMarkdown>
              )}
            </div>
          </div>
        ))}

        {/* --- Loader --- */}
        {isLoading && (
          <div className="flex justify-start">
              <div className="px-3 py-2 sm:p-3 rounded-lg bg-white border">
                  <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                      <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce"></span>
                      <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce [animation-delay:0.15s]"></span>
                      <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce [animation-delay:0.3s]"></span>
                  </div>
                  <span className="text-sm text-gray-500 font-medium">{t("aiCurriculumAssistant.aiThinking")}</span>
                  </div>
              </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* --- INPUT FOOTER --- */}
      <div className="flex-shrink-0 mt-4">
          <div className="flex items-end space-x-2 border border-gray-300 rounded-lg p-2">
            <textarea
              ref={textareaRef}
              rows="1"
              placeholder="Ask the assistant..."
              className="flex-grow bg-transparent focus:outline-none p-2 text-sm resize-none"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyPress}
            />
            <button
              onClick={handleSend}
              className="bg-indigo-600 text-white font-semibold py-2 px-3 sm:px-4 rounded-md hover:bg-indigo-700 transition text-sm self-end"
            >
                {t("aiCurriculumAssistant.send")}
            </button>
          </div>
      </div>
    </div>
  );
};

export default AICurriculumAssistant;